<template>
  <apex-chart :type="type" :height="height" :series="series" :options="chartOptions" />
</template>

<script>
import ApexChart from 'vue-apexcharts'

const formatMoney = val => (val === null || val === undefined ? '-' : '$' + Math.round(val).toLocaleString())
const formatCompact = val => (Math.abs(val) >= 1000 ? `$${Math.round(val / 1000)}k` : `$${Math.round(val)}`)

export default {
  components: { ApexChart },

  props: {
    // c3 style columns from the api: [['x', ...categories], [name, ...values], ...]
    columns: { type: Array, default: () => [] },
    type: { type: String, default: 'line' },
    height: { type: [Number, String], default: 400 },
    stacked: { type: Boolean, default: false },
    horizontal: { type: Boolean, default: false },
    // Only stack these series together, the rest are shown side by side
    stackGroup: { type: Array, default: null },
    colors: { type: Object, default: null },
    // [{ value, label, color }]
    targets: { type: Array, default: () => [] }
  },

  computed: {
    categories () {
      const x = this.columns.find(([name]) => name === 'x') || []

      return x.slice(1)
    },

    series () {
      return this.columns
        .filter(([name]) => name !== 'x')
        .map(([name, ...data]) => ({
          name,
          data: data.map(value => (value === null ? null : Number(value))),
          ...(this.stackGroup ? { group: this.stackGroup.includes(name) ? 'stacked' : name } : {})
        }))
    },

    // Apex only scales to the series, so include the targets in the axis max
    maxValue () {
      const targetMax = Math.max(0, ...this.targets.map(({ value }) => Number(value) || 0))

      if (!targetMax) {
        return undefined
      }

      const seriesMax = Math.max(0, ...this.series.flatMap(({ data }) => data.filter(v => v !== null)))

      return Math.max(seriesMax, targetMax) * 1.05
    },

    chartOptions () {
      const valueAxis = {
        max: this.maxValue,
        labels: {
          formatter: formatCompact,
          style: { colors: '#a0a4b8', fontSize: '11px' }
        }
      }

      return {
        chart: {
          stacked: this.stacked || !!this.stackGroup,
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: 'inherit'
        },
        colors: this.colors ? this.series.map(({ name }) => this.colors[name]) : undefined,
        plotOptions: {
          bar: { horizontal: this.horizontal, columnWidth: '60%', barHeight: '60%' }
        },
        stroke: this.type === 'line' ? { width: 3, curve: 'smooth' } : { width: 0 },
        markers: { size: this.type === 'line' ? 3 : 0 },
        dataLabels: { enabled: false },
        legend: { position: 'top', horizontalAlign: 'right', fontSize: '12px' },
        xaxis: {
          categories: this.categories,
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: this.horizontal
            ? { formatter: formatCompact, style: { colors: '#a0a4b8', fontSize: '11px' } }
            : { style: { colors: '#8b8fa7', fontSize: '12px' } },
          ...(this.horizontal ? { max: this.maxValue } : {})
        },
        yaxis: this.horizontal
          ? { labels: { style: { colors: '#8b8fa7', fontSize: '12px' } } }
          : valueAxis,
        grid: {
          borderColor: '#f0f1f7',
          xaxis: { lines: { show: this.horizontal } },
          yaxis: { lines: { show: !this.horizontal } }
        },
        tooltip: {
          shared: true,
          intersect: false,
          theme: 'dark',
          y: { formatter: formatMoney }
        },
        annotations: {
          yaxis: this.targets.map(({ value, label, color }) => ({
            y: value,
            borderColor: color,
            strokeDashArray: 4,
            label: {
              text: `${label} ${formatCompact(value)}`,
              position: 'left',
              textAnchor: 'start',
              borderColor: color,
              style: { color: '#fff', background: color, fontSize: '10px', fontWeight: 600 }
            }
          }))
        }
      }
    }
  }
}
</script>
